import type { JobPhase, Lead } from '@prisma/client';
import { JobCategories, LeadOrigin as PrismaLeadOrigin, LeadStage as PrismaLeadStage } from '@prisma/client';
import { titlifyEnum } from '@utils/stringHelpers';
import z from 'zod';

import { LEAD_FILTER_NONE, matchesEnumFilter, matchesIdFilter } from '~/server/schema/lead.filters';
import type { ReasonOptionChoice } from '~/server/schema/reasonOption.schema';
import { formatReasonOption } from '~/server/schema/reasonOption.schema';

// Lead stages — derived directly from the Prisma LeadStage enum (single source of truth).
export const leadStageEnum = z.enum(PrismaLeadStage);
export type LeadStage = z.infer<typeof leadStageEnum>;

// The open sub-stages a lead can be moved directly into, by a board drag or the table's stage menu.
// QUALIFIED is reached through job creation and DISQUALIFIED through the reason modal, so a direct stage
// write is restricted to these.
export const openLeadStageEnum = leadStageEnum.exclude(['QUALIFIED', 'DISQUALIFIED']);
export type OpenLeadStage = z.infer<typeof openLeadStageEnum>;

export const isOpenLeadStage = (stage: LeadStage): stage is OpenLeadStage => (openLeadStageEnum.options as LeadStage[]).includes(stage);

/**
 * The stages a user-driven stage write may start FROM: every stage but QUALIFIED. A qualified lead owns a
 * real job, so its stage stops being the user's to change — `updateLeadStage` would leave that job pointing
 * at a lead claiming to be unworked, and `disqualifyLead` would leave it pointing at one claiming to be
 * rejected. Both pass this; only job creation writes a stage without a guard.
 *
 * DISQUALIFIED is in the set deliberately, and both procedures need it there: it is the reopen path the
 * board's drag and the table's stage menu offer, and it is the change-the-reason path `disqualifyLead`
 * doubles as.
 *
 * Excluded from `leadStageEnum` rather than added to `openLeadStageEnum`, and the direction is the point:
 * this is a Prisma `where`, so it must match what the column holds, not what this release may write. A
 * stage mid-retirement leaves `openLeadStageEnum` while rows are still parked at it, and a list built up
 * from that would silently refuse to move them. See rule 2 under "Enum values have three rules, not one"
 * in docs/leads-redesign.md.
 */
export const UNCONVERTED_FROM_STAGES = leadStageEnum.exclude(['QUALIFIED']).options;

// Where a lead came from — derived from the Prisma LeadOrigin enum, like the stages above.
export const leadOriginEnum = z.enum(PrismaLeadOrigin);
export type LeadOrigin = z.infer<typeof leadOriginEnum>;

// Spelled out rather than run through `titlifyEnum`, which would render "Reach Ai Voice" and
// "Partner Api". Total, so a new origin is a compile error here instead of a blank cell.
export const LEAD_ORIGIN_LABELS: Record<LeadOrigin, string> = {
	ALIVO: 'Alivo',
	MANUAL: 'Manual',
	PARTNER_API: 'Partner API',
	QUICK_ESTIMATE: 'Quick Estimate',
	REACH_AI_VOICE: 'AI Voice',
	WEBSITE_FORM: 'Website Form',
};

/** How a stage is named where it heads something: a board column, a table tab, a call log's lead chip. */
export const LEAD_STAGE_LABELS: Record<LeadStage, string> = {
	CONTACTED: 'Contacted',
	DISQUALIFIED: 'Unqualified - Rejected',
	NEW: 'New Lead',
	QUALIFIED: 'Qualified - Bid Opened',
};

/**
 * How a stage is named in front of a `stageChangedAt` — the prefix on a board card, and the table's
 * stage-date column header. Spelled separately from `LEAD_STAGE_LABELS`, whose entries are headings
 * ("Unqualified - Rejected") and read wrong in front of a date.
 *
 * NEW is "Moved back" because that is the only way a lead in NEW has a `stageChangedAt` later than its
 * creation: dragged back from an open stage, or reopened from a closed one. Not a past participle like the
 * other three — "Reopened" is false for the drag, and "Returned", inches from a card's phone number, reads
 * as "returned the call".
 */
export const LEAD_STAGE_CHANGED_LABELS: Record<LeadStage, string> = {
	CONTACTED: 'Contacted',
	DISQUALIFIED: 'Disqualified',
	NEW: 'Moved back',
	QUALIFIED: 'Qualified',
};

export type LeadOwnerOption = { firstName: string; id: number; lastName: string };

export type LeadFull = Lead & {
	branch?: { id: number; name: string } | null;
	// The reason row behind `disqualifiedReasonId`. Null when the lead carries free text instead (a
	// business with no list, or the quick-estimate cascade), or no reason at all.
	disqualifiedReasonRef?: ReasonOptionChoice | null;
	// `jobPhase` required, not optional: `getAllLeads` is the only query that loads `job` at all, so there is
	// no second producer to keep it honest — making it optional would let a future select drop it silently.
	job?: { id: number; deletedAt?: Date | null; jobPhase: JobPhase } | null;
	owner?: LeadOwnerOption | null;
	// Loaded by `getAllLeads`, which the board and the table share; `deletedAt` flags a soft-deleted linked
	// quick estimate, which is what decides between a live link to it and a static badge.
	quickEstimate?: { deletedAt: Date | null } | null;
	referralSourceOption?: { id: number; name: string } | null;
};

/**
 * The reason to display for a disqualified lead. Shared by the board, the table and CSV export so a
 * reason reads identically everywhere.
 *
 * Note this carries the note as well as the name (`name — note`) for a `requiresFreeText` reason, so
 * anything filtering or grouping wants the name alone, not this.
 */
export const getLeadReasonLabel = (lead: LeadFull): string | null =>
	formatReasonOption({ name: lead.disqualifiedReasonRef?.name, text: lead.disqualifiedReason });

export type LeadFilters = {
	branchIds: number[];
	origins: LeadOrigin[];
	ownerIds: number[];
	referralSourceOptionIds: number[];
};

export { LEAD_FILTER_NONE };

export const EMPTY_LEAD_FILTERS: LeadFilters = { branchIds: [], origins: [], ownerIds: [], referralSourceOptionIds: [] };

/** Owner, origin, branch and referral-source filters shared by the board and the table. An empty selection matches everything. */
export const leadMatchesFilters = (lead: LeadFull, filters: LeadFilters): boolean =>
	matchesIdFilter(filters.branchIds, lead.branchId) &&
	matchesEnumFilter(filters.origins, lead.origin) &&
	matchesIdFilter(filters.ownerIds, lead.ownerId) &&
	matchesIdFilter(filters.referralSourceOptionIds, lead.referralSourceOptionId);

/**
 * Case-insensitive match against everything the board card shows, so the search box above the board
 * and the table's quick filter agree on which leads a term finds.
 */
export const leadMatchesSearch = (lead: LeadFull, search: string): boolean => {
	const term = search.trim().toLowerCase();
	if (!term) return true;

	const haystack = [
		lead.name,
		lead.email ?? '',
		lead.phone ?? '',
		lead.address ?? '',
		lead.description ?? '',
		lead.owner ? `${lead.owner.firstName} ${lead.owner.lastName}` : '',
		lead.branch?.name ?? '',
		lead.referralSourceOption?.name ?? '',
		LEAD_ORIGIN_LABELS[lead.origin],
		lead.jobType ? titlifyEnum(lead.jobType) : '',
		getLeadReasonLabel(lead) ?? '',
	];

	return haystack.some((value) => value.toLowerCase().includes(term));
};

export const hasContact = (lead: { email?: string | null; phone?: string | null }): boolean => !!lead.email || !!lead.phone;

const CONTACT_REQUIRED = { message: 'Enter an email or a phone number', path: ['email'] };

// A blank contact field means absent, and absent is NULL — the form posts `''` for every field it
// renders, so without this an untouched Edit would write `''` back over a lead's NULLs.
const blankToNull = (value: string | null | undefined): string | null => value || null;

// Same rule, except `undefined` has to survive it: on `updateLeadSchema` an omitted key means "leave this
// column alone" while `''` means "clear it", and `blankToNull` would collapse the two into NULL and blank
// every field the caller never mentioned.
const blankToNullIfPresent = (value: string | null | undefined): string | null | undefined => (value === undefined ? undefined : blankToNull(value));

export class LeadSchema {
	/**
	 * A lead entered by hand from the Leads page or the "Create New" menu. Unlike the embedded form,
	 * only a name and one way to reach the person are required — the rest is often unknown at first
	 * contact and gets filled in as the conversation progresses.
	 */
	static leadFieldsSchema = z.object({
		address: z.string().trim().max(500).nullish().transform(blankToNull),
		branchId: z.number().nullable().optional(),
		description: z.string().trim().max(5000).nullish().transform(blankToNull),
		email: z
			.union([z.email().max(255), z.literal('')])
			.nullish()
			.transform(blankToNull),
		// "Job Type", carried onto `Job.category` at conversion. Often unknown at first contact.
		jobType: z.enum(JobCategories).nullable().default(null),
		name: z.string().trim().min(1, { message: 'Name is required' }).max(255),
		// Omitted: the server assigns the creator. Null: explicitly unassigned.
		ownerId: z.number().nullable().optional(),
		phone: z.string().trim().max(50).nullish().transform(blankToNull),
		referralSourceOptionId: z.number().nullable().default(null),
	});

	static manualLeadSchema = LeadSchema.leadFieldsSchema.refine(hasContact, CONTACT_REQUIRED);

	/**
	 * Omitted fields are left as they are, so nothing defaults here — which is why the contact fields use
	 * `blankToNullIfPresent` rather than `blankToNull`: on this schema `undefined` and `''` have to stay
	 * distinguishable all the way to the Prisma call, where `undefined` leaves the column and `null`
	 * clears it.
	 *
	 * No contact is required either: quick-estimate leads can arrive without one and must stay editable.
	 * The router refuses only removing a lead's last way to reach it, and keys that check on `undefined`
	 * for the same reason.
	 */
	static updateLeadSchema = z.object({
		address: z.string().trim().max(500).nullish().transform(blankToNullIfPresent),
		branchId: z.number().nullable().optional(),
		description: z.string().trim().max(5000).nullish().transform(blankToNullIfPresent),
		email: z
			.union([z.email().max(255), z.literal('')])
			.nullish()
			.transform(blankToNullIfPresent),
		id: z.number(),
		jobType: z.enum(JobCategories).nullable().optional(),
		name: z.string().trim().min(1, { message: 'Name is required' }).max(255).optional(),
		ownerId: z.number().nullable().optional(),
		phone: z.string().trim().max(50).nullish().transform(blankToNullIfPresent),
		referralSourceOptionId: z.number().nullable().optional(),
	});

	static embeddedFormSchema = z.object({
		businessUuid: z.uuid(),
		name: z.string(),
		email: z.email(),
		phone: z.string(),
		address: z.string(),
		description: z.string(),
		// Required in the form's UI, optional here: a visitor's browser can be holding a cached copy of
		// `form.html` from before the field existed, and the form drops the field entirely when it cannot
		// load the business's job types. Neither should cost us the lead.
		jobType: z.enum(JobCategories).nullable().optional(),
	});

	/**
	 * Partners usually hold the address already split into parts, but some only have one line. Accept
	 * either and normalize to the single free-text `Lead.address` column at the service boundary.
	 *
	 * Accepting both from day one is the cheap direction: a partner that starts by sending a string can
	 * move to the object later without us cutting a v2.
	 */
	static partnerAddressSchema = z.union([
		z.string().trim().max(500),
		z.object({
			city: z.string().trim().max(100).optional(),
			state: z.string().trim().max(50).optional(),
			street1: z.string().trim().max(200).optional(),
			street2: z.string().trim().max(200).optional(),
			zip: z.string().trim().max(20).optional(),
		}),
	]);

	/**
	 * Request body for `POST /api/public/v1/leads`.
	 *
	 * Note what is absent: there is no `businessId` or `businessUuid`. Tenancy comes from the API key and
	 * nothing else, so no request body can steer a lead into another business.
	 *
	 * Only `name` is structurally required, plus at least one of `email`/`phone` — a partner integration
	 * that 400s on a missing middle field is a partner integration that gets abandoned, but a lead nobody
	 * can contact is not a lead.
	 */
	static partnerLeadSchema = z
		.object({
			address: LeadSchema.partnerAddressSchema.optional(),
			description: z.string().trim().max(5000).optional(),
			email: z.email().max(255).optional(),
			// Supplying this opts the partner into idempotent retries, matched against the partial unique
			// index on (businessId, externalSystem, externalId). Omitting it means every POST creates a new
			// lead, which is documented loudly in docs/partner-lead-api.md.
			externalId: z.string().trim().min(1).max(255).optional(),
			// Defaulted rather than left nullable on purpose: NULLs never collide in a Postgres unique
			// index, so a null here would silently disable deduplication and hand the partner duplicates
			// with no error to tell them why.
			externalSystem: z.string().trim().min(1).max(100).default('partner-api'),
			name: z.string().trim().min(1).max(255),
			phone: z.string().trim().max(50).optional(),
			// Free text, not `ReferralSource` — see the comment on `Lead.source` in schema.prisma. An
			// unknown value must never 400 a partner over a field this cosmetic.
			source: z.string().trim().min(1).max(100).optional(),
		})
		.refine(hasContact, {
			message: 'Provide at least one of `email` or `phone` — a lead with no way to contact it is not actionable.',
		});
}
